import { getApiUrl } from "src/utils";
import {
  CxmCreatePayoutAccountPayload,
  CxmDirection,
  CxmMethod,
  CxmPayoutAccount,
  CxmPayoutAccountRequirements,
  CxmQuote,
  CxmQuotePreviewPayload,
  CxmSupportedOptionsResponse,
} from "./types";

export enum CxmEndpoints {
  SUPPORTED_OPTIONS = "/psp/cxm/supported-options",
  QUOTE_PREVIEW = "/psp/cxm/quote-preview",
  PAYOUT_ACCOUNTS = "/psp/cxm/payout-accounts",
  PAYOUT_ACCOUNT_REQUIREMENTS = "/psp/cxm/payout-accounts/requirements",
  // Deposit initiation is not exposed yet: the payment row only exists once
  // the gateway settles, so correlating the callback needs a side table that
  // the backend does not have yet. The types are already here for it.
}

/**
 * `max.amount === "0"` means the method has no ceiling. Read literally it
 * caps every payment at zero, which silently disables the method — so limits
 * must always go through here rather than being compared inline.
 */
export function methodLimits(method: CxmMethod): {
  min: number | null;
  max: number | null;
} {
  const min = Number(method.limits?.min?.amount);
  const max = Number(method.limits?.max?.amount);
  return {
    min: Number.isFinite(min) && min > 0 ? min : null,
    max: Number.isFinite(max) && max > 0 ? max : null,
  };
}

/**
 * The gateway's `quote` endpoint is an FX calculator, not a validator — it
 * returns 200 for amounts above the maximum and below the minimum alike, and
 * only `create` refuses them. Validate here so the user is not given a green
 * light followed by a rejection.
 */
export function validateAmount(
  method: CxmMethod,
  amount: string,
): string | null {
  if (!/^\d+(\.\d+)?$/.test(amount.trim())) return "Enter an amount.";
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "Enter an amount.";

  const { min, max } = methodLimits(method);
  if (min !== null && value < min) {
    return `The minimum for ${method.displayName} is ${min} ${method.currency}.`;
  }
  if (max !== null && value > max) {
    return `The maximum for ${method.displayName} is ${max} ${method.currency}.`;
  }
  return null;
}

/** Strips the stray trailing bracket in type 17's holder-name label. */
export function fieldLabel(name: string): string {
  return name.replace(/\)\)$/, ")");
}

export function visibleFields(
  requirements: CxmPayoutAccountRequirements,
): CxmPayoutAccountField[] {
  return (requirements.fields ?? []).filter((f) => !f.hidden);
}

/**
 * The gateway validates payout details not at all — it stores `{}` and
 * unknown keys as `approved`, and a wrong key produces an account it cannot
 * read, so the payout later leaves without a beneficiary. The backend
 * enforces this too; doing it here as well keeps the error next to the field
 * instead of arriving as a server rejection.
 */
export function validatePayoutDetails(
  requirements: CxmPayoutAccountRequirements,
  details: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of visibleFields(requirements)) {
    if (!String(details?.[field.name] ?? "").trim()) {
      errors[field.name] = `${fieldLabel(field.name)} is required.`;
    }
  }
  return errors;
}

type CxmPayoutAccountField = CxmPayoutAccountRequirements["fields"][number];

/**
 * Talks to the vakotrade backend, which signs and proxies to the CXM partner
 * gateway. The widget never holds `cxm_request_secret`, and never sends a
 * user id — the backend takes it from the auth token.
 */
export class CxmService {
  private apiUrl: string;
  private authorization?: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl ?? getApiUrl() ?? "";
  }

  public setToken(token?: string): void {
    if (token) this.authorization = token;
  }

  private async request<T = any>(
    method: "GET" | "POST" | "DELETE",
    endpoint: string,
    options?: {
      body?: Record<string, any>;
      params?: Record<string, string>;
    },
  ): Promise<T> {
    const queryString = options?.params
      ? "?" + new URLSearchParams(options.params).toString()
      : "";

    const response = await fetch(
      `${this.apiUrl.replace("/graphql", "")}${endpoint}${queryString}`,
      {
        method,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...(this.authorization ? { Authorization: this.authorization } : {}),
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
      },
    );

    if (!response.ok) {
      const errorBody = await response.text();
      let detail: string | null = null;
      try {
        const parsed = JSON.parse(errorBody) as {
          message?: string;
          error?: { code?: string; message?: string };
        };
        // The backend forwards the gateway's `{error:{code,message}}` shape
        // for upstream failures, and its own `{message}` otherwise.
        detail = parsed?.error?.message ?? parsed?.message ?? null;
      } catch {
        // not JSON — keep the status line
      }
      console.warn(
        `CXM request failed: ${response.status} ${response.statusText}. ${errorBody}`,
      );
      throw new Error(detail ?? `HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as T;
  }

  /**
   * The catalogue is personalised per user and can come back empty when the
   * operator has granted no payment rules — that is a legitimate state, not
   * an error, and the UI should say so rather than spin.
   */
  public async getMethods(direction: CxmDirection): Promise<CxmMethod[]> {
    const res = await this.request<CxmSupportedOptionsResponse>(
      "GET",
      CxmEndpoints.SUPPORTED_OPTIONS,
      { params: { direction } },
    );
    return res?.methods ?? [];
  }

  public quotePreview(payload: CxmQuotePreviewPayload): Promise<CxmQuote> {
    return this.request<CxmQuote>("POST", CxmEndpoints.QUOTE_PREVIEW, {
      body: payload,
    });
  }

  public getPayoutAccountRequirements(
    paymentMethodTypeId: number,
  ): Promise<CxmPayoutAccountRequirements> {
    return this.request<CxmPayoutAccountRequirements>(
      "GET",
      CxmEndpoints.PAYOUT_ACCOUNT_REQUIREMENTS,
      { params: { paymentMethodTypeId: String(paymentMethodTypeId) } },
    );
  }

  /** Returns a bare array, not an `{items}` envelope. */
  public async listPayoutAccounts(): Promise<CxmPayoutAccount[]> {
    const res = await this.request<CxmPayoutAccount[]>(
      "GET",
      CxmEndpoints.PAYOUT_ACCOUNTS,
    );
    return Array.isArray(res) ? res : [];
  }

  public createPayoutAccount(
    payload: CxmCreatePayoutAccountPayload,
  ): Promise<{ id: number }> {
    return this.request<{ id: number }>("POST", CxmEndpoints.PAYOUT_ACCOUNTS, {
      body: payload,
    });
  }

  public deletePayoutAccount(id: number): Promise<unknown> {
    return this.request("DELETE", CxmEndpoints.PAYOUT_ACCOUNTS, {
      body: { id },
    });
  }

}

export const cxmService = new CxmService();
